import React, { useEffect, useState, useRef, useCallback } from "react";
import { Footer, Navbar } from "../components";
import api from "../utils/base_url";
import { useNavigate } from "react-router-dom";
import "../styles/AboutPage.css";

/* ═══════════════════════════════════════════════════════════
   REVEAL HOOK
═══════════════════════════════════════════════════════════ */
const useReveal = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("visible");
          obs.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
};

/* ═══════════════════════════════════════════════════════════
   PREMIUM ARTISAN SVG BADGES — Cashew Cream & Roasted Amber
═══════════════════════════════════════════════════════════ */
const IconCashewNut = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="14" fill="var(--primary-50)" stroke="var(--primary-200)" strokeWidth="1.5" />
    <path
      d="M19 15 C14 17, 12 23, 15 29 C18 35, 25 37, 31 34 C36 31, 38 25, 34 20 C31 17, 27 19, 25 22 C23 25, 20 26, 18 23 C16 20, 18 17, 21 16 Z"
      fill="var(--primary-500)"
    />
    <circle cx="29" cy="19" r="2" fill="var(--secondary-400)" />
  </svg>
);

const IconQualityShield = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="14" fill="var(--primary-50)" stroke="var(--primary-200)" strokeWidth="1.5" />
    <path
      d="M24 13 L33 17 V24 C33 29.5, 29 34.5, 24 36 C19 34.5, 15 29.5, 15 24 V17 L24 13 Z"
      fill="none"
      stroke="var(--primary-600)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 24.5 L23.5 27 L28 21.5"
      stroke="var(--secondary-500)"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconVacuumSeal = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="14" fill="var(--primary-50)" stroke="var(--primary-200)" strokeWidth="1.5" />
    <rect x="16" y="18" width="16" height="18" rx="3" stroke="var(--primary-600)" strokeWidth="2" fill="none" />
    <line x1="16" y1="22" x2="32" y2="22" stroke="var(--secondary-500)" strokeWidth="2" />
    <circle cx="24" cy="28" r="2.5" fill="var(--primary-400)" />
  </svg>
);

const IconRoasting = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    <rect width="48" height="48" rx="14" fill="var(--primary-50)" stroke="var(--primary-200)" strokeWidth="1.5" />
    <path
      d="M16 29 C16 25, 20 22, 24 22 C28 22, 32 25, 32 29 C32 33, 28 35, 24 35 C20 35, 16 33, 16 29 Z"
      fill="none"
      stroke="var(--primary-600)"
      strokeWidth="2"
    />
    <path
      d="M22 20 Q24 15 22 12 M26 20 Q28 15 26 12 M18 21 Q20 17 18 14"
      stroke="var(--secondary-500)"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

/* Small pillar icons */
const PillarIcon = ({ emoji }) => (
  <div className="about-pillar-icon-box">
    <span>{emoji}</span>
  </div>
);

/* Leaf bullet */
const LeafBullet = () => (
  <div className="about-mission-bullet">✦</div>
);

/* Decorative brand divider */
const SectionDivider = ({ center = false }) => (
  <div className="about-divider" style={center ? { justifyContent: "center" } : {}}>
    <div className="about-divider-line-l" />
    <div className="about-divider-gem">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <polygon points="10,2 18,10 10,18 2,10" fill="var(--primary-300)" opacity="0.3" />
        <polygon points="10,5 15,10 10,15 5,10" fill="var(--primary-400)" />
        <circle cx="10" cy="10" r="2.5" fill="var(--primary-100)" />
      </svg>
    </div>
    <div className="about-divider-line-r" />
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN ABOUT PAGE COMPONENT
═══════════════════════════════════════════════════════════ */
const AboutPage = () => {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  const go = useCallback((id) => navigate("/product", { state: { categoryId: id } }), [navigate]);

  useEffect(() => {
    api.get("/categories/")
      .then(r => { if (r.data?.data) setCategories(r.data.data); })
      .catch(console.error);
  }, []);

  const repeated = [...categories, ...categories, ...categories];

  /* Reveal refs */
  const heroRef    = useReveal();
  const nameRef    = useReveal();
  const diffRef    = useReveal();
  const missionRef = useReveal();
  const servRef    = useReveal();

  const pillars = [
    { emoji: "🥜", label: "Grade-A Jumbo Cashews", desc: "Whole, crisp, nutrient-dense kernels selected by hand." },
    { emoji: "🌿", label: "100% Pure & Natural",    desc: "Zero chemical bleaches, no preservatives or artificial polish." },
    { emoji: "🏡", label: "Native Farm Heritage",    desc: "Directly sourced from Cuddalore's fertile native soil." },
    { emoji: "❤️", label: "Thousands of Families",   desc: "Delivering wholesome health & trusted crunch every day." },
  ];

  const services = [
    {
      Icon: IconCashewNut,
      title: "Whole & Flavoured Cashews",
      desc: "Finest plain whole kernels, slow-roasted, Himalayan salt, cracked pepper, and artisan spice blends."
    },
    {
      Icon: IconQualityShield,
      title: "Native Natural Foods",
      desc: "Authentic country delights, traditional wood-pressed oils, palm jaggery, and chemical-free pantry staples."
    },
    {
      Icon: IconVacuumSeal,
      title: "Nitrogen Flush Sealed",
      desc: "Hermetically sealed multi-layer pouches lock out moisture, preserving peak crispness, aroma, and nutrients."
    },
    {
      Icon: IconRoasting,
      title: "Artisan Small-Batch Roasting",
      desc: "Gentle traditional roasting methods that preserve natural heart-healthy oils without burning."
    },
  ];

  const badges = [
    { icon: "🌿", title: "100% Pure & Natural", sub: "No Preservatives" },
    { icon: "🥜", title: "Handpicked Grade-A",   sub: "Whole Cashews" },
    { icon: "🏆", title: "Trusted Brand",        sub: "5000+ Happy Homes" },
    { icon: "📦", title: "Vacuum Sealed",        sub: "Guaranteed Crunch" },
  ];

  const missions = [
    "To bring <strong>premium, farm-fresh cashews and native natural foods</strong> directly to every family table.",
    "To foster <strong>wholesome, preservative-free snacking</strong> that nourishes your body with genuine earth goodness.",
    "To honor and uphold <strong>traditional harvesting, sun-drying, and artisanal roasting practices</strong>.",
    "To uphold unmatched standards of <strong>crunch, aroma, and hygiene</strong> from native farm to doorstep.",
  ];

  return (
    <>
      <Navbar />
      <main className="about-page">

        {/* ══════════ HERO SECTION ══════════ */}
        <section ref={heroRef} className="about-hero about-reveal">
          <div className="about-hero-bg-glow" />
          <div className="about-hero-frame1" />
          <div className="about-hero-frame2" />

          {/* Corner gold accents */}
          {[
            { top: "18px", left: "18px" }, { top: "18px", right: "18px" },
            { bottom: "18px", left: "18px" }, { bottom: "18px", right: "18px" }
          ].map((pos, i) => (
            <svg
              key={i}
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              style={{ position: "absolute", zIndex: 3, ...pos }}
            >
              <polygon points="9,0 18,9 9,18 0,9" fill="var(--primary-300)" opacity="0.6" />
              <circle cx="9" cy="9" r="3" fill="var(--primary-500)" />
            </svg>
          ))}

          <div className="about-hero-content">
            <div className="about-hero-est">
              <div className="about-hero-est-line" />
              <p className="about-hero-est-text">Rooted in Nature · Pure Living</p>
              <div className="about-hero-est-line" />
            </div>

            {/* Emblem medallion with logo */}
            <div className="about-hero-logo-wrap">
              <div className="about-hero-emblem-ring">
                <img src="/MSk.png" alt="MSk Native Naturals" className="about-hero-logo" />
              </div>
            </div>

            <h1 className="about-hero-brand-name">MSk Native Naturals</h1>
            <p className="about-hero-tagline">
              Premium Cashews &middot; Native Delights &middot; Pure Goodness
            </p>
            <p className="about-hero-sub">
              Sourced from Tamil Nadu's fertile native groves in Cuddalore — harvested with traditional care, 
              gently roasted to perfection, and delivered fresh to your doorstep.
            </p>

            {/* Pill Highlights */}
            <div className="about-hero-pills">
              <span className="about-hero-pill">🥜 Grade-A Whole Cashews</span>
              <span className="about-hero-pill">🌿 100% Chemical-Free</span>
              <span className="about-hero-pill">📦 Vacuum-Sealed Freshness</span>
            </div>
          </div>

          <svg aria-hidden className="about-hero-wave" viewBox="0 0 1440 64" preserveAspectRatio="none">
            <path d="M0,0 C360,64 1080,0 1440,64 L1440,64 L0,64Z" fill="var(--primary-900)" />
          </svg>
        </section>

        {/* ══════════ BRAND STORY SECTION (DARK MOCHA) ══════════ */}
        <section ref={nameRef} className="about-dark-section about-reveal">
          <div className="about-dark-section-dot-bg" />
          <div className="about-section-inner" style={{ textAlign: "center", position: "relative" }}>
            <p className="about-eyebrow light">The Story Behind the Name</p>
            <h2 className="about-section-title light">Why MSk Native Naturals?</h2>
            <SectionDivider center />

            <div className="about-name-words">
              <div className="about-name-word-card">
                <span className="about-name-badge">Heritage</span>
                <h3 className="about-name-word-text">MSk</h3>
                <p className="about-name-word-meaning">Tradition &middot; Trust &middot; Quality</p>
              </div>

              <div className="about-name-divider-plus">+</div>

              <div className="about-name-word-card highlight">
                <span className="about-name-badge">Identity</span>
                <h3 className="about-name-word-text">Native Naturals</h3>
                <p className="about-name-word-meaning">100% Pure &middot; Authentic Snacking</p>
              </div>
            </div>

            <p className="about-body-text light" style={{ textAlign: "center", maxWidth: "680px", margin: "0 auto" }}>
              Born from our deep-rooted conviction that food should remain exactly as nature intended — 
              free from artificial polish, chemical bleaching, and synthetic preservatives.{" "}
              <strong style={{ color: "var(--primary-200)" }}>MSk Native Naturals</strong> represents our dedication to
              bringing you the authentic aroma, genuine crunch, and wholesome nutrition of native farmlands.
            </p>
          </div>
        </section>

        {/* ══════════ WHAT MAKES US DIFFERENT ══════════ */}
        <section ref={diffRef} className="about-light-section about-reveal">
          <div style={{ maxWidth: "980px", margin: "0 auto" }}>
            <p className="about-eyebrow dark" style={{ textAlign: "center" }}>Our Craft & Standards</p>
            <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
              What Makes MSk Different?
            </h2>
            <SectionDivider center />

            <div className="about-diff-box">
              <h3 className="about-diff-box-title">
                Handpicked With Care &middot; Delivered With Pride
              </h3>
              <p className="about-body-text dark">
                Our journey began with a clear, uncompromising realization: in a market overflowing with artificially bleached,
                chemically treated, and stale nuts, families deserve genuine <strong>crunch, honest grades, and farm-fresh nutrition</strong>.
              </p>
              <p className="about-body-text dark" style={{ marginBottom: 0 }}>
                Every single batch at <strong>MSk Native Naturals</strong> is meticulously inspected, sorted by kernel grade,
                and vacuum-sealed immediately after roasting so you enjoy the full sweetness and hearty aroma of genuine native cashews.
              </p>
            </div>

            <div className="about-pillars-grid">
              {pillars.map(({ emoji, label, desc }, i) => (
                <div key={i} className="about-pillar-card">
                  <div className="about-pillar-icon">
                    <PillarIcon emoji={emoji} />
                  </div>
                  <p className="about-pillar-label">{label}</p>
                  <p className="about-pillar-desc">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════ MISSION SECTION (DARK MOCHA) ══════════ */}
        <section ref={missionRef} className="about-dark-section about-reveal" style={{ textAlign: "center" }}>
          <div className="about-dark-section-dot-bg" />
          <div className="about-section-inner" style={{ position: "relative" }}>
            <p className="about-eyebrow light">Our Commitment</p>
            <h2 className="about-section-title light">
              Wholesome Snacking, Pure Living
            </h2>
            <SectionDivider center />

            <div className="about-mission-list">
              {missions.map((text, i) => (
                <div key={i} className="about-mission-item">
                  <LeafBullet />
                  <p
                    className="about-mission-text"
                    dangerouslySetInnerHTML={{
                      __html: text.replace(/<strong>/g, '<strong style="color:var(--primary-200)">')
                    }}
                  />
                </div>
              ))}
            </div>

            <p className="about-mission-closing">
              Pure nutrition for your family, nurtured with traditional integrity. 🥜🌿
            </p>
          </div>
        </section>

        {/* ══════════ SERVICES / PRODUCT SPECTRUM ══════════ */}
        <section ref={servRef} className="about-light-section about-services-section about-reveal">
          <p className="about-eyebrow dark" style={{ textAlign: "center" }}>What We Offer</p>
          <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
            Our Cashew &amp; Natural Product Spectrum
          </h2>
          <SectionDivider center />

          <div className="about-services-grid">
            {services.map(({ Icon, title, desc }, i) => (
              <div key={i} className="about-service-card">
                <div className="about-service-icon">
                  <Icon />
                </div>
                <h3 className="about-service-title">{title}</h3>
                <p className="about-service-desc">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ══════════ TRUST BADGES STRIP ══════════ */}
        <div className="about-badges-strip">
          <div className="about-badges-grid">
            {badges.map(({ icon, title, sub }, i) => (
              <div key={i} className="about-badge-item">
                <div className="about-badge-icon">{icon}</div>
                <p className="about-badge-title">{title}</p>
                <p className="about-badge-sub">{sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════ CATEGORY MARQUEE ══════════ */}
        {categories.length > 0 && (
          <section className="about-collections-section">
            <div className="about-collections-head">
              <p className="about-eyebrow dark" style={{ textAlign: "center" }}>Handpicked For You</p>
              <h2 className="about-section-title dark" style={{ textAlign: "center" }}>
                Explore Our Collections
              </h2>
              <SectionDivider center />
            </div>

            <div className="about-marquee-wrap">
              <div className="about-marquee">
                {repeated.map((cat, i) => (
                  <div key={i} className="about-cat-card" onClick={() => go(cat.id)}>
                    <div className="about-cat-img">
                      <img
                        src={cat.category_image || "https://via.placeholder.com/300x200"}
                        alt={cat.category_name}
                      />
                    </div>
                    <div className="about-cat-label">
                      <span>{cat.category_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </main>
      <Footer />
    </>
  );
};

export default AboutPage;